import React, { useState, useEffect, useRef } from 'react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';

interface ColorSelectorProps {
  defaultColor?: 'red' | 'green' | 'yellow' | '';
  onColorChange?: (color: 'red' | 'green' | 'yellow') => void;
  disabled?: boolean;
}

const colorMap: { [key: string]: string } = {
  red: 'Rot',
  green: 'Grün',
  yellow: 'Gelb',
};

const TeeSelector: React.FC<ColorSelectorProps> = ({
  defaultColor = '',
  onColorChange,
  disabled = false,
}) => {
  const [selectedColor, setSelectedColor] = useState(defaultColor);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleColorSelect = (color: 'red' | 'green' | 'yellow') => {
    if (!disabled) {
      setSelectedColor(color);
      setIsOpen(false);
      if (onColorChange) {
        onColorChange(color);
      }
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (defaultColor) {
      setSelectedColor(defaultColor);
    }
  }, [defaultColor]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`bg-white border cursor-pointer hover:border-gray-400 text-xs h-9 hover:shadow-md shadow-sm rounded-md flex items-center gap-2 px-2 ${disabled && 'cursor-not-allowed hover:border-transparent bg-gray-300 opacity-30'} ${colorMap[selectedColor] && 'border-gray-500 and hover:border-gray-600'}`}
        onClick={toggleDropdown}
      >
        <div className="grid gap-0.5">
          <div
            className={`transition-all w-1 h-1 rounded-full bg-green-400 ${selectedColor === 'green' ? 'opacity-100 !h-3' : 'opacity-80'}`}
          ></div>
          <div
            className={`transition-all w-1 h-1 rounded-full bg-red-400 ${selectedColor === 'red' ? 'opacity-100 !h-3' : 'opacity-80'}`}
          ></div>
          <div
            className={`transition-all w-1 h-1 rounded-full bg-yellow-400 ${selectedColor === 'yellow' ? 'opacity-100 !h-3' : 'opacity-80'}`}
          ></div>
        </div>
        <div className={`font-medium min-w-[56px]`}>
          {colorMap[selectedColor] || 'Abschlag'}
        </div>
        <ChevronUpDownIcon className="w-4" />
      </div>
      {isOpen && (
        <div className="animate__animated animate__fadeIn animate__faster absolute z-10 p-1 w-[90px] bg-white border border-gray-400 top-10 right-0 rounded-md shadow-md">
          {['green', 'red', 'yellow'].map((color) => (
            <div
              key={color}
              className={`flex rounded-md gap-2 text-xs pl-1 pr-2 py-1 items-center cursor-pointer  ${colorMap[selectedColor] === colorMap[color] ? 'bg-white border border-gray-300 shadow-md' : 'hover:bg-gray-100 opacity-90'}`}
              onClick={() =>
                handleColorSelect(color as 'red' | 'green' | 'yellow')
              }
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5"
              >
                <path
                  d="M7 19.0001V17.0001H17V19.0001H15C14.5 19.0001 14 19.2001 13.6 19.6001C13.2 20.0001 13 20.5001 13 21.0001V22.0001H11V21.0001C11 20.5001 10.8 20.0001 10.4 19.6001C10 19.2001 9.5 19.0001 9 19.0001H7ZM11 6.0001C11 5.7001 11.1 5.5001 11.3 5.3001C11.5 5.1001 11.7 5.0001 12 5.0001C12.3 5.0001 12.5 5.1001 12.7 5.3001C12.9 5.5001 13 5.7001 13 6.0001C13 6.3001 12.9 6.5001 12.7 6.7001C12.5 6.9001 12.3 7.0001 12 7.0001C11.7 7.0001 11.5 6.9001 11.3 6.7001C11.1 6.5001 11 6.3001 11 6.0001ZM13 8.0001C13 7.7001 13.1 7.5001 13.3 7.3001C13.5 7.1001 13.7 7.0001 14 7.0001C14.3 7.0001 14.5 7.1001 14.7 7.3001C14.9 7.5001 15 7.7001 15 8.0001C15 8.3001 14.9 8.5001 14.7 8.7001C14.5 8.9001 14.3 9.0001 14 9.0001C13.7 9.0001 13.5 8.9001 13.3 8.7001C13.1 8.5001 13 8.3001 13 8.0001ZM9 8.0001C9 7.7001 9.1 7.5001 9.3 7.3001C9.5 7.1001 9.7 7.0001 10 7.0001C10.3 7.0001 10.5 7.1001 10.7 7.3001C10.9 7.5001 11 7.7001 11 8.0001C11 8.3001 10.9 8.5001 10.7 8.7001C10.5 8.9001 10.3 9.0001 10 9.0001C9.7 9.0001 9.5 8.9001 9.3 8.7001C9.1 8.5001 9 8.3001 9 8.0001ZM16.9 4.1001C15.5 2.7001 13.9 2.1001 12 2.1001C10.1 2.1001 8.4 2.8001 7.1 4.1001C5.8 5.4001 5 7.1001 5 9.0001C5 10.9001 5.7 12.6001 7 13.9001C8.3 15.2001 10 15.9001 11.9 15.9001C13.8 15.9001 15.5 15.2001 16.8 13.9001C18.1 12.6001 18.8 10.9001 18.8 9.0001C18.8 7.1001 18.3 5.4001 16.9 4.1001ZM15.5 12.5001C14.5 13.5001 13.3 14.0001 12 14.0001C10.7 14.0001 9.4 13.5001 8.5 12.5001C7.6 11.5001 7 10.4001 7 9.0001C7 7.6001 7.5 6.4001 8.5 5.5001C9.5 4.6001 10.6 4.0001 12 4.0001C13.4 4.0001 14.6 4.5001 15.5 5.5001C16.4 6.5001 17 7.6001 17 9.0001C17 10.4001 16.5 11.6001 15.5 12.5001Z"
                  fill={color === 'yellow' ? 'orange' : color || '#cccccc'}
                />
              </svg>

              <div className="font-medium">{colorMap[color]}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeeSelector;
